/**
 * @file api-platform-integrations.ts
 * @description API-клиент для управления внешними интеграциями платформы
 * (phase08). Используется на `IntegrationsPage` под `PLATFORM_MANAGE_INTEGRATIONS`.
 */

import { apiFetch } from "./api-client";

export type PlatformIntegrationKind =
  | "DADATA"
  | "SMSRU"
  | "UNISENDER"
  | "SMTP"
  | "SDEK"
  | "YANDEX_MAPS"
  | "YANDEX_S3"
  | "TELEGRAM_BOT"
  | "AVITO"
  | "VK"
  | "OTHER";

/**
 * Порядок видов на странице «Общие интеграции» и в меню «Подключить» — один
 * список на весь кабинет: новый вид = строка здесь и запись в
 * `INTEGRATION_KIND_META` (инцидент: вид не появился в UI из-за второго списка).
 */
export const INTEGRATION_KIND_ORDER: readonly PlatformIntegrationKind[] = [
  "DADATA",
  "SMSRU",
  "UNISENDER",
  "SMTP",
  "SDEK",
  // phase19.12 — карта пунктов выдачи: виджет СДЭК и карта ПВЗ в админке.
  "YANDEX_MAPS",
  "YANDEX_S3",
  "TELEGRAM_BOT",
  "AVITO",
  "VK",
  "OTHER",
];

export type PlatformIntegrationStatus = "NOT_CONFIGURED" | "ACTIVE" | "PAUSED" | "ERROR";

export interface PlatformIntegrationDto {
  id: string;
  kind: PlatformIntegrationKind;
  /** null — платформенная (для всех магазинов), FK на Shop — конкретный магазин. */
  shopId: string | null;
  shop: { id: string; slug: string; displayName: string } | null;
  name: string;
  status: PlatformIntegrationStatus;
  config: Record<string, unknown> | null;
  lastSyncAt: string | null;
  errorMessage: string | null;
  isPrimary: boolean;
  hasCredentials: boolean;
  /** phase19.28 (PL13): политика вида — «только своя» / «общая допустима». */
  policy?: "ONLY_OWN" | "SHARED_ALLOWED";
  /** Общая настройка вида «только своя» — её нужно перенести в магазин. */
  needsTransfer?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIntegrationPayload {
  kind: PlatformIntegrationKind;
  shopId?: string | null;
  name: string;
  credentials?: Record<string, unknown>;
  config?: Record<string, unknown>;
  isPrimary?: boolean;
}

export interface UpdateIntegrationPayload {
  shopId?: string | null;
  name?: string;
  credentials?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
  status?: PlatformIntegrationStatus;
  isPrimary?: boolean;
}

export interface IntegrationUsageDayDto {
  date: string; // YYYY-MM-DD
  source: string;
  count: number;
  errorCount: number;
}

export const platformIntegrations = {
  list: () =>
    apiFetch<PlatformIntegrationDto[]>("/admin/platform/integrations"),

  findOne: (id: string) =>
    apiFetch<PlatformIntegrationDto>(`/admin/platform/integrations/${id}`),

  create: (payload: CreateIntegrationPayload) =>
    apiFetch<PlatformIntegrationDto>("/admin/platform/integrations", {
      method: "POST",
      json: payload,
    }),

  update: (id: string, payload: UpdateIntegrationPayload) =>
    apiFetch<PlatformIntegrationDto>(`/admin/platform/integrations/${id}`, {
      method: "PATCH",
      json: payload,
    }),

  remove: (id: string) =>
    apiFetch<void>(`/admin/platform/integrations/${id}`, { method: "DELETE" }),

  /** Дневная статистика запросов (phase12.0.1). */
  usage: (id: string, days = 30) =>
    apiFetch<IntegrationUsageDayDto[]>(
      `/admin/platform/integrations/${id}/usage?days=${days}`,
    ),

  /** phase19.5 — живая проверка подключения (баланс/OAuth/SMTP verify). */
  test: (id: string) =>
    apiFetch<IntegrationTestResult>(`/admin/platform/integrations/${id}/test`, {
      method: "POST",
    }),
};

/** Результат проверки подключения (phase19.5). */
export interface IntegrationTestResult {
  ok: boolean;
  message: string;
  latencyMs: number;
}

/** Метаданные kind для UI (label + поля credentials + поля config). */
export interface IntegrationKindMeta {
  kind: PlatformIntegrationKind;
  label: string;
  description: string;
  credentialFields: Array<{
    key: string;
    label: string;
    type: "text" | "password" | "textarea";
    helperText?: string;
    required?: boolean;
  }>;
  configFields?: Array<{
    key: string;
    label: string;
    type: "text" | "number" | "checkbox";
    helperText?: string;
  }>;
}

export const INTEGRATION_KIND_META: Record<PlatformIntegrationKind, IntegrationKindMeta> = {
  DADATA: {
    kind: "DADATA",
    label: "DaData",
    description:
      "Подсказывает и проверяет адреса и находит реквизиты организаций по ИНН — при заведении клиентов и юрлиц.",
    credentialFields: [
      {
        key: "apiKey",
        label: "API-ключ",
        type: "password",
        required: true,
        helperText: "Личный кабинет DaData → «Профиль» → «API-ключ».",
      },
      {
        key: "secretKey",
        label: "Секретный ключ (необязательно)",
        type: "password",
        helperText: "Там же, в профиле DaData. Нужен для проверки и исправления адресов.",
      },
    ],
    configFields: [
      {
        key: "quotaDailyLimit",
        label: "Дневной лимит запросов (по подписке)",
        type: "number",
        helperText:
          "Бесплатный тариф — 10 000, «Маркетолог» — от 50 000. Нужен, чтобы видеть в статистике, сколько запросов осталось; 0 или пусто — не показывать.",
      },
    ],
  },
  SMSRU: {
    kind: "SMSRU",
    label: "SMS.RU",
    description: "Отправляет SMS: одноразовые коды для входа покупателей и уведомления.",
    credentialFields: [
      {
        key: "apiId",
        label: "API ID",
        type: "password",
        required: true,
        helperText: "Кабинет SMS.RU → «Программистам» → «Ваш API ID».",
      },
      {
        key: "sender",
        label: "Имя отправителя (зарегистрированное в SMS.RU)",
        type: "text",
        helperText: "Если не задано — используется системное имя SMS.RU.",
      },
    ],
    configFields: [
      {
        key: "quotaDailyLimit",
        label: "Дневной лимит SMS (по балансу/тарифу)",
        type: "number",
        helperText: "Нужен, чтобы видеть в статистике, сколько осталось; 0 или пусто — не показывать.",
      },
    ],
  },
  UNISENDER: {
    kind: "UNISENDER",
    label: "Unisender",
    description: "Отправляет письма покупателям: подтверждения заказов, уведомления и рассылки.",
    credentialFields: [
      {
        key: "apiKey",
        label: "Ключ доступа к API",
        type: "password",
        required: true,
        helperText: "Кабинет Unisender → «Настройки» → «Интеграция и API».",
      },
    ],
    configFields: [
      {
        key: "defaultFrom",
        label: "Email отправителя по умолчанию",
        type: "text",
      },
      {
        key: "quotaDailyLimit",
        label: "Дневной лимит писем (по тарифу)",
        type: "number",
        helperText: "Нужен, чтобы видеть в статистике, сколько осталось; 0 или пусто — не показывать.",
      },
    ],
  },
  YANDEX_S3: {
    kind: "YANDEX_S3",
    label: "Yandex Object Storage",
    description: "Облачное хранилище Яндекса для резервных копий базы и файлов вне сервера.",
    credentialFields: [
      {
        key: "accessKeyId",
        label: "Идентификатор ключа (Access Key ID)",
        type: "password",
        required: true,
        helperText: "Консоль Yandex Cloud → сервисный аккаунт → «Создать статический ключ доступа».",
      },
      { key: "secretAccessKey", label: "Секретный ключ (Secret Access Key)", type: "password", required: true },
    ],
    configFields: [
      { key: "bucket", label: "Бакет (имя хранилища)", type: "text" },
      { key: "region", label: "Регион", type: "text", helperText: "Обычно ru-central1." },
    ],
  },
  TELEGRAM_BOT: {
    kind: "TELEGRAM_BOT",
    label: "Telegram-бот",
    description: "Бот в Telegram, через который клиенты пишут магазину; сообщения попадают в кабинет.",
    credentialFields: [
      {
        key: "botToken",
        label: "Токен бота",
        type: "password",
        required: true,
        helperText: "Выдаёт @BotFather в Telegram при создании бота (команда /newbot).",
      },
    ],
    configFields: [
      { key: "username", label: "Имя бота (без @)", type: "text" },
      {
        key: "webhookUrl",
        label: "Адрес для входящих сообщений (Webhook URL)",
        type: "text",
        helperText: "Адрес сервера, куда Telegram присылает сообщения. Обычно задаёт администратор сервера.",
      },
    ],
  },
  AVITO: {
    kind: "AVITO",
    label: "Avito",
    description:
      "Связь с Авито: объявления, заказы с Авито Доставкой и отзывы попадают в кабинет магазина.",
    credentialFields: [
      {
        key: "clientId",
        label: "Client ID",
        type: "password",
        required: true,
        helperText: "Кабинет Авито → «Настройки» → «Для профессионалов» → «API» → ваше приложение.",
      },
      {
        key: "clientSecret",
        label: "Client Secret",
        type: "password",
        required: true,
      },
      {
        key: "sellerUserId",
        label: "Номер профиля продавца (необязательно)",
        type: "text",
        helperText: "Номер профиля продавца на Авито. Пока не используется — можно оставить пустым.",
      },
    ],
  },
  VK: {
    kind: "VK",
    label: "ВКонтакте",
    description: "Связь с сообществом ВКонтакте: сообщения и товары магазина во ВКонтакте.",
    credentialFields: [
      {
        key: "accessToken",
        label: "Ключ доступа сообщества",
        type: "password",
        required: true,
        helperText: "Сообщество → «Управление» → «Работа с API» → «Создать ключ».",
      },
    ],
    configFields: [
      { key: "groupId", label: "Номер сообщества (ID)", type: "text" },
      { key: "marketplaceSyncEnabled", label: "Выгружать каталог в товары ВКонтакте", type: "checkbox" },
    ],
  },
  SMTP: {
    kind: "SMTP",
    label: "SMTP (Email)",
    description:
      "Отправка писем покупателям через почтовый сервер (например, вашей почты на домене).",
    credentialFields: [
      {
        key: "host",
        label: "Адрес почтового сервера (SMTP)",
        type: "text",
        required: true,
        helperText: "Есть в настройках почты: например smtp.yandex.ru.",
      },
      { key: "port", label: "Порт", type: "text", required: true, helperText: "Обычно 465 или 587." },
      { key: "user", label: "Логин", type: "text", required: true },
      { key: "password", label: "Пароль", type: "password", required: true },
    ],
    configFields: [
      { key: "fromAddress", label: "Адрес отправителя", type: "text" },
      { key: "fromName", label: "Имя отправителя", type: "text" },
      { key: "secure", label: "Защищённое соединение (порт 465)", type: "checkbox" },
    ],
  },
  SDEK: {
    kind: "SDEK",
    label: "СДЭК (доставка)",
    description:
      "Доставка СДЭК: заявки на отправку, выбор пункта выдачи и статусы посылок. " +
      "Карта пунктов на витрине работает вместе с «Яндекс.Картами» — без их ключа покупатель " +
      "выбирает пункт из списка.",
    credentialFields: [
      {
        key: "clientId",
        label: "Идентификатор (Client ID)",
        type: "password",
        required: true,
        helperText: "Личный кабинет СДЭК → «Интеграция» → ключи доступа к API.",
      },
      { key: "clientSecret", label: "Секретный ключ (Client Secret)", type: "password", required: true },
      { key: "accountNumber", label: "Номер договора", type: "text", required: true },
    ],
    configFields: [
      { key: "sandboxMode", label: "Тестовый режим СДЭК (без настоящих отправлений)", type: "checkbox" },
      {
        key: "webhookSecret",
        label: "Секрет для статусов посылок (необязательно)",
        type: "text",
        helperText: "Проверяет, что уведомления о статусах пришли именно от СДЭК.",
      },
    ],
  },
  YANDEX_MAPS: {
    kind: "YANDEX_MAPS",
    label: "Яндекс.Карты",
    description:
      "Карта пунктов выдачи для покупателей на витрине и для сотрудников в кабинете.",
    credentialFields: [
      {
        key: "apiKey",
        label: "API-ключ",
        type: "text",
        required: true,
        helperText:
          "Кабинет разработчика Яндекса → ключ «JavaScript API и HTTP Геокодер»; ограничьте его адресом вашего сайта.",
      },
      { key: "apiSecret", label: "Секрет подписи запросов (необязательно)", type: "password" },
    ],
    configFields: [],
  },
  OTHER: {
    kind: "OTHER",
    label: "Иная интеграция",
    description: "Сервис, которого нет в списке: ключи задаются вручную по инструкции разработчика.",
    credentialFields: [
      {
        key: "rawConfig",
        label: "Ключи доступа (в формате JSON)",
        type: "textarea",
        helperText: "Заполняет разработчик, подключающий сервис.",
      },
    ],
  },
};
